<template>
    <div class="container">
        <h1 class="mt-3">{{ title }}</h1>

        <div class="input-group mb-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('task.searchPlaceholder')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <button class="input-group-text bg-body" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </button>
        </div>

        <!-- Planned-time filters: four month presets plus a custom range, only
             one of the two in effect at a time. -->
        <div v-if="showDateFilters" class="mb-3">
            <FilterGroups :filterGroups="dateFilterGroups" @filters-changed="onDateFilterChanged" />
            <VueDatePicker auto-apply format="dd.MM.yyyy" v-model="customRange" :locale="locale"
                :cancel-text="$t('common.cancel')" :select-text="$t('common.select')" :range="{}"
                :clearable="true" :closeOnScroll="true" :placeholder="$t('dateFilters.customRange')"
                @update:modelValue="onCustomRangeChanged" />
        </div>

        <!-- Sort picker. The label lives here rather than in FilterGroups' own
             group.name, which renders raw text and would not follow a locale switch. -->
        <div v-if="showSortFilters" class="mb-3">
            <div class="fw-bold mb-1 small">{{ $t('sortFilters.label') }}</div>
            <FilterGroups :filterGroups="sortFilterGroups" @filters-changed="onSortFilterChanged" />
        </div>

        <!-- Filter Groups -->
        <FilterGroups v-if="showFilters" :filterGroups="filterGroups" @filters-changed="onFiltersChanged" />

        <TaskList :tasks="tasks" />

        <ListPagination :currentPage="currentPage" :totalTasks="totalTasks" :limit="limit"
            @page-changed="onPageChanged" />
    </div>
</template>

<script>
import axios from '@/services/axios';
import { useI18n } from 'vue-i18n';
import { startOfMonth, endOfMonth, subMonths, addMonths } from 'date-fns';
import TaskList from '@/components/TaskList.vue';
import ListPagination from '@/components/ListPagination.vue';
import FilterGroups from '@/components/FilterGroups.vue';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useDate } from '@/composables/useDate';
import { TASK_FILTER_KEYS_STORAGE_KEY, TASK_DATE_FILTER_STORAGE_KEY, TASK_SORT_FILTER_STORAGE_KEY } from '@/utils/taskFilters';

export default {
    name: 'TasksListComponent',
    components: { TaskList, ListPagination, FilterGroups, VueDatePicker },
    props: {
        title: String,
        apiUrl: String,
        showFilters: { type: Boolean, default: false },
        // Opt-in planned-time (estimated_date) filter bar, separate from the
        // stage/priority one so /tasks keeps exactly what it had.
        showDateFilters: { type: Boolean, default: false },
        // Opt-in sort picker. Without it the list keeps whatever sort_by the
        // view baked into apiUrl.
        showSortFilters: { type: Boolean, default: false }
    },
    setup() {
        const { locale } = useI18n();
        const { formatDate } = useDate();
        return { locale, formatDate };
    },
    data() {
        return {
            tasks: [],
            currentPage: 1,        // mirror of ?page=
            limit: 10,
            totalTasks: 0,
            searchTerm: '',        // bound to input; mirrors ?search=
            activeFilters: [],
            lastRouteKey: '',     // prevents duplicate fetches when multiple query keys change together

            activeDateKey: null,  // one of the preset keys below, or null
            customRange: null,    // [Date, Date] from the picker, or null

            activeSortKey: null,  // key of a sortFilterGroups pill; null only when the view's sort_by matches none

            sortFilterGroups: [
                {
                    name: '',
                    multi: false,
                    // One pill always stays on, so the list's order is never hidden.
                    required: true,
                    filters: [
                        { key: 'dueDate', labelKey: 'sortFilters.dueDate', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', sortParams: { sort_by: 'due_date', sort_order: 'asc' } },
                        { key: 'estimatedDate', labelKey: 'sortFilters.estimatedDate', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', sortParams: { sort_by: 'estimated_date', sort_order: 'asc' } },
                        // When the user was added to the task. The API needs exactly
                        // one id in users, otherwise it falls back to creation time.
                        { key: 'newestFirst', labelKey: 'sortFilters.newestFirst', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', sortParams: { sort_by: 'assigned_on', sort_order: 'desc' } },
                    ]
                }
            ],

            dateFilterGroups: [
                {
                    name: '',
                    multi: false,
                    filters: [
                        { key: 'untilMonthEnd', labelKey: 'dateFilters.untilMonthEnd', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary' },
                        { key: 'lastMonth', labelKey: 'dateFilters.lastMonth', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary' },
                        { key: 'thisMonth', labelKey: 'dateFilters.thisMonth', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary' },
                        { key: 'nextMonth', labelKey: 'dateFilters.nextMonth', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary' },
                    ]
                }
            ],

            /* your filterGroups unchanged */
            filterGroups: [
                {
                    name: '',
                    multi: false,
                    filters: [
                        { key: 'stage0', labelKey: 'enums.stage.0', active: false, activeClass: 'bg-warning-subtle text-warning-emphasis', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { stage_id: '0', finished: '0' } },
                        { key: 'stage1', labelKey: 'enums.stage.1', active: false, activeClass: 'bg-info-subtle text-info-emphasis', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { stage_id: '1', finished: '0' } },
                        { key: 'stage2', labelKey: 'enums.stage.2', active: false, activeClass: 'bg-success-subtle text-success-emphasis', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { stage_id: '2', finished: '0' } },
                        { key: 'finished', labelKey: 'enums.stage.finished', active: false, activeClass: 'bg-success-subtle text-success-emphasis', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { finished: '1' } },
                    ]
                },
                {
                    name: '',
                    multi: true,
                    filters: [
                        { key: 'priority1', labelKey: 'enums.priority.1', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { priority_id: '1' } },
                        { key: 'priority2', labelKey: 'enums.priority.2', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { priority_id: '2' } },
                        { key: 'priority3', labelKey: 'enums.priority.3', active: false, activeClass: 'text-bg-primary', inactiveClass: 'bg-body-tertiary text-body-secondary', params: { priority_id: '3' } }
                    ]
                }
            ]
        };
    },

    mounted() {
        // Load saved filters on first render (does NOT fetch)
        if (this.showFilters) this.loadSelectedFilters();
        if (this.showDateFilters) this.loadDateFilter();
        if (this.showSortFilters) this.loadSortFilter();
    },

    watch: {
        // Single place that reacts to URL changes (both page + search together).
        '$route.query': {
            immediate: true,
            deep: true,
            handler(newQ) {
                // normalize page
                const pRaw = newQ.page ?? '1';
                const p = parseInt(pRaw, 10);
                if (!Number.isFinite(p) || p < 1) {
                    this.$router.replace({ query: { ...newQ, page: '1' } });
                    return; // wait for the normalized route to re-trigger
                }

                // normalize search
                const s = (newQ.search ?? '').toString();

                // keep input in sync with URL
                if (this.searchTerm !== s) this.searchTerm = s;

                const key = `${p}|${s}`;
                if (key === this.lastRouteKey) return; // prevent duplicate fetches
                this.lastRouteKey = key;

                this.currentPage = p;
                this.fetchTasks(p, s);
            }
        }
    },

    methods: {
        // User pressed enter or clicked the search button
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query, page: '1' };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q });
            // Do NOT call fetch here; watcher will fetch once.
        },

        onFiltersChanged(activeFilters) {
            this.activeFilters = activeFilters;
            // Reset to first page when filters change, keep current search
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.saveSelectedFilters();
            this.fetchTasks(1, this.searchTerm);
            // Do NOT call fetch here; watcher will fetch once.
        },

        onDateFilterChanged(activeFilters) {
            this.activeDateKey = activeFilters[0]?.key ?? null;
            // A preset and a custom range are mutually exclusive.
            if (this.activeDateKey) this.customRange = null;
            this.applyDateFilter();
        },

        onCustomRangeChanged() {
            // partial-range emits after the first click; wait for the second.
            if (Array.isArray(this.customRange) && !this.hasCustomRange()) return;

            if (this.hasCustomRange()) {
                this.dateFilterGroups[0].filters.forEach(f => (f.active = false));
                this.activeDateKey = null;
            }
            this.applyDateFilter();
        },

        applyDateFilter() {
            this.saveDateFilter();
            // Reset to first page when filters change, keep current search
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.fetchTasks(1, this.searchTerm);
        },

        onSortFilterChanged(activeFilters) {
            // activeFilters carries every active pill in the component, so pick
            // out this group's rather than trusting position.
            const sortKeys = this.sortFilterGroups[0].filters.map(f => f.key);
            this.activeSortKey = activeFilters.find(f => sortKeys.includes(f.key))?.key ?? null;
            this.applySortFilter();
        },

        applySortFilter() {
            this.saveSortFilter();
            // Reset to first page when the order changes, keep current search
            const q = { ...this.$route.query, page: '1' };
            this.$router.push({ query: q });
            this.fetchTasks(1, this.searchTerm);
        },

        // Empty when nothing is picked, so the sort_by the view baked into
        // apiUrl stands untouched.
        currentSortParams() {
            const pill = this.sortFilterGroups[0].filters.find(f => f.key === this.activeSortKey);
            return pill ? pill.sortParams : {};
        },

        // The pill matching the sort_by the view baked into apiUrl, so the
        // default order is shown before the user has picked anything.
        defaultSortKey() {
            const sortBy = new URLSearchParams(this.apiUrl.split('?')[1] || '').get('sort_by');
            return this.sortFilterGroups[0].filters.find(f => f.sortParams.sort_by === sortBy)?.key ?? null;
        },

        saveSortFilter() {
            if (this.activeSortKey) {
                localStorage.setItem(TASK_SORT_FILTER_STORAGE_KEY, JSON.stringify({ key: this.activeSortKey }));
            } else {
                localStorage.removeItem(TASK_SORT_FILTER_STORAGE_KEY);
            }
        },

        loadSortFilter() {
            const filters = this.sortFilterGroups[0].filters;
            let key = null;
            const stored = localStorage.getItem(TASK_SORT_FILTER_STORAGE_KEY);
            if (stored) {
                try {
                    key = JSON.parse(stored)?.key ?? null;
                } catch (err) {
                    console.warn('Could not parse saved sort filter:', err);
                }
            }
            if (!filters.some(f => f.key === key)) key = this.defaultSortKey();

            this.activeSortKey = key;
            filters.forEach(f => {
                f.active = f.key === key;
            });
        },

        hasCustomRange() {
            // auto-apply hands us a one-element array between the two clicks.
            return Array.isArray(this.customRange)
                && this.customRange.length === 2
                && !!this.customRange[0]
                && !!this.customRange[1];
        },

        monthParams(date) {
            return {
                estimated_date_start: this.formatDate(startOfMonth(date)),
                estimated_date_end: this.formatDate(endOfMonth(date))
            };
        },

        // Resolved per request rather than baked into the filter objects, so a
        // session left open across a month boundary never sends a stale window.
        currentDateParams() {
            if (this.hasCustomRange()) {
                return {
                    estimated_date_start: this.formatDate(this.customRange[0]),
                    estimated_date_end: this.formatDate(this.customRange[1])
                };
            }

            const now = new Date();
            switch (this.activeDateKey) {
                // No start bound: everything planned up to the end of this
                // month, overdue and older work included.
                case 'untilMonthEnd': return { estimated_date_end: this.formatDate(endOfMonth(now)) };
                case 'thisMonth': return this.monthParams(now);
                case 'lastMonth': return this.monthParams(subMonths(now, 1));
                case 'nextMonth': return this.monthParams(addMonths(now, 1));
                default: return {};
            }
        },

        saveDateFilter() {
            let stored = null;
            if (this.hasCustomRange()) {
                stored = { from: this.formatDate(this.customRange[0]), to: this.formatDate(this.customRange[1]) };
            } else if (this.activeDateKey) {
                stored = { key: this.activeDateKey };
            }

            if (stored) {
                localStorage.setItem(TASK_DATE_FILTER_STORAGE_KEY, JSON.stringify(stored));
            } else {
                localStorage.removeItem(TASK_DATE_FILTER_STORAGE_KEY);
            }
        },

        loadDateFilter() {
            const stored = localStorage.getItem(TASK_DATE_FILTER_STORAGE_KEY);
            if (!stored) return;
            try {
                const saved = JSON.parse(stored);
                if (saved && saved.from && saved.to) {
                    // Midday avoids any DST edge shifting the day back.
                    this.customRange = [new Date(saved.from + 'T12:00:00'), new Date(saved.to + 'T12:00:00')];
                    this.activeDateKey = null;
                } else if (saved && saved.key) {
                    this.customRange = null;
                    this.activeDateKey = saved.key;
                }
                this.dateFilterGroups[0].filters.forEach(f => {
                    f.active = f.key === this.activeDateKey;
                });
            } catch (err) {
                console.warn('Could not parse saved date filter:', err);
            }
        },

        // Pagination click from ListPagination
        onPageChanged(newPage) {
            const q = { ...this.$route.query, page: String(newPage) };
            if (q.page !== this.$route.query.page) {
                this.$router.push({ query: q });
            }
        },

        async fetchTasks(page = 1, searchTerm = '') {
            try {
                // Parsed rather than concatenated: apiUrl already carries the
                // view's own sort_by, and set() replaces it instead of sending
                // the param twice. URLSearchParams encodes on its own, so
                // nothing here may be encodeURIComponent'd as well.
                const [path, baseQuery = ''] = this.apiUrl.split('?');
                const params = new URLSearchParams(baseQuery);
                params.set('page', page);
                params.set('limit', this.limit);
                params.set('search', searchTerm);

                if (this.showFilters) this.loadSelectedFilters();
                if (this.showFilters) {
                    const mergedParams = this.mergeFilterParams(this.activeFilters);
                    Object.entries(mergedParams).forEach(([key, value]) => {
                        params.set(key, value);
                    });
                }

                if (this.showDateFilters) {
                    // Reloaded here too: the immediate $route.query watcher
                    // fires its first fetch before mounted() runs.
                    this.loadDateFilter();
                    Object.entries(this.currentDateParams()).forEach(([key, value]) => {
                        params.set(key, value);
                    });
                }

                if (this.showSortFilters) {
                    this.loadSortFilter();
                    Object.entries(this.currentSortParams()).forEach(([key, value]) => {
                        params.set(key, value);
                    });
                }

                const response = await axios.get(`${path}?${params.toString()}`);
                this.tasks = response.data.data;
                this.totalTasks = response.data.total;
                this.currentPage = page;
            } catch (error) {
                console.error(`Error fetching tasks for ${this.title}:`, error);
            }
        },

        mergeFilterParams(filters) {
            const paramMap = {};
            filters.forEach(f => {
                Object.entries(f.params).forEach(([key, rawValue]) => {
                    const values = String(rawValue).split(',');
                    (paramMap[key] ||= []).push(...values);
                });
            });

            const merged = {};
            Object.keys(paramMap).forEach(key => {
                const uniqueValues = [...new Set(paramMap[key])];
                merged[key] = uniqueValues.join(',');
            });
            return merged;
        },

        saveSelectedFilters() {
            const activeKeys = this.activeFilters.map(f => f.key);
            localStorage.setItem(TASK_FILTER_KEYS_STORAGE_KEY, JSON.stringify(activeKeys));
        },

        loadSelectedFilters() {
            const stored = localStorage.getItem(TASK_FILTER_KEYS_STORAGE_KEY);
            if (!stored) return;
            try {
                const keysArray = JSON.parse(stored);
                this.filterGroups.forEach(group => {
                    group.filters.forEach(filter => {
                        filter.active = keysArray.includes(filter.key);
                    });
                });
            } catch (err) {
                console.warn('Could not parse saved filter keys:', err);
            }
            this.collectActiveFilters();
        },

        collectActiveFilters() {
            const active = [];
            this.filterGroups.forEach(group => {
                group.filters.forEach(filter => {
                    if (filter.active) active.push(filter);
                });
            });
            this.activeFilters = active;
        }
    }
};
</script>
